import loadable from "@loadable/component";
import clsx from "clsx";

const Connector = loadable(() => import("@micado-digital/react-form/Connector"));
const Container = loadable(() => import("@Components/views/Container"));
const Form = loadable(() => import("@micado-digital/react-form"));
const ReactCopyrights01 = loadable(() =>
	import("@micado-digital/react-copyrights/ReactCopyrights01")
);
const ReactFile01 = loadable(() => import("@micado-digital/react-file/ReactFile01"));
const ReactHeadline01 = loadable(() =>
	import("@micado-digital/react-headline/ReactHeadline01")
);
const ReactHTML = loadable(() => import("@micado-digital/react-html/ReactHTML"));
const ReactText01 = loadable(() => import("@micado-digital/react-text/ReactText01"));
const ReactLegacy = loadable(() => import("@micado-digital/react-legacy/ReactLegacy"));
const List = loadable(() => import("@Components/Elements/List"));
const Tours = loadable(() => import("@Components/Elements/Tours"));
const PaymentSuccess = loadable(() => import("@Components/Elements/PaymentSuccess"));
const RegistrationConfirmation = loadable(() =>
	import("@Components/Elements/RegistrationConfirmation")
);

const getJSXElement = (element, lang) => {
	const { REACT_APP_PATH } = process.env;
	const { id, tag } = element;

	switch (tag) {
		case "basic-headline": {
			const { text, variant = 0 } = element;

			let config = {};

			switch (variant) {
				case 0: {
					config = {
						component: "h1",
						variants: {
							xs: "display-medium",
							md: "display-large"
						}
					};
					break;
				}
				case 1: {
					config = {
						component: "h2",
						variant: "display-medium"
					};
					break;
				}
				case 2: {
					config = {
						component: "h3",
						variant: "display-small"
					};
					break;
				}
				case 3: {
					config = {
						component: "h4",
						variant: "headline-large"
					};
					break;
				}
				case 4: {
					config = {
						component: "h5",
						variant: "headline-medium"
					};
					break;
				}
				case 5: {
					config = {
						component: "h6",
						variant: "headline-small"
					};
					break;
				}
				case 6: {
					config = {
						component: "p",
						variant: "title-small"
					};
					break;
				}
				default: {
					break;
				}
			}

			return (
				<Container key={id} tag={tag}>
					<ReactHeadline01
						component={config?.component}
						text={text}
						variant={config?.variant}
						variants={config?.variants}
					/>
				</Container>
			);
		}

		case "basic-text": {
			const { text, variant } = element;

			return (
				<Container
					className={clsx({
						"mco-view-component-container-basic-text-variant-1": variant === 1
					})}
					key={id}
					tag={tag}
				>
					<ReactText01 text={text} variant="body-medium" />
				</Container>
			);
		}

		case "basic-html": {
			const { text } = element;

			return (
				<Container key={id} tag={tag}>
					<ReactHTML key={id} html={text} />
				</Container>
			);
		}

		case "basic-download-group": {
			const { children } = element;

			return (
				<Container key={id} tag={tag}>
					{children?.map(item => getJSXElement(item))}
				</Container>
			);
		}

		case "basic-download": {
			const { media, title } = element;

			return (
				<Container key={id} tag={tag}>
					<ReactFile01 media={media} title={title} />
				</Container>
			);
		}

		case "basic-copyrights": {
			return (
				<Container key={id} tag={tag}>
					<ReactCopyrights01 />
				</Container>
			);
		}

		case "basic-imprint": {
			return (
				<Container key={id} tag={tag}>
					<ReactLegacy lang={lang} logoIdentifier="logo" />
				</Container>
			);
		}

		case "basic-form": {
			const { elements } = element;

			return (
				<Container key={id} tag={tag}>
					<Form action={`${REACT_APP_PATH}/submitform.json.api`} method="POST">
						<Connector formElements={elements} formID={id} />
					</Form>
				</Container>
			);
		}

		case "basic-list": {
			const { textcontent: { items = [] } = {}, title } = element;

			return (
				<Container key={id} tag={tag}>
					<List items={items} title={title} />
				</Container>
			);
		}

		case "tm-kat-booking": {
			const { elements = [] } = element;

			const ids = elements?.map(element => {
				const { textcontent: { items = [] } = {} } = element;

				const id = items?.find(item => item.name === "tourid")?.text;

				return id;
			});

			return (
				<Container key={id} tag={tag}>
					<Tours lang={lang} tourIDs={ids} />
				</Container>
			);
		}

		case "tm-kat-book-success": {
			return (
				<Container key={id} tag={tag}>
					<PaymentSuccess lang={lang} />
				</Container>
			);
		}

		case "tm-kat-registration-confirmation": {
			return (
				<Container key={id} tag={tag}>
					<RegistrationConfirmation lang={lang} />
				</Container>
			);
		}

		default:
			if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
				console.warn("missing element:", element);
			}
			return null;
	}
};

export default getJSXElement;
